import { retryCancelablePromise } from '../core/utils/retryCancelablePromise';
export class AuthService {
    constructor(httpRequest) {
        Object.defineProperty(this, "httpRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: httpRequest
        });
    }
    /**
     * Get login initialization config
     * @returns LoginConfiguration Login configuration.
     * @throws ApiError
     */
    getConfig({ loginHint, loginChallenge, contentType, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/sso', headers: {
                'Content-Type': contentType,
            }, query: {
                'loginHint': loginHint,
                'loginChallenge': loginChallenge,
            }, errors: {
                302: `Redirects to identity provider so user can log in.`,
                422: `Return redirectUrl to already known signOnUrl if no other option is available.`,
            } }, options)), options);
    }
    /**
     * Callback endpoint for internal identity provider
     * @returns RedirectNeededError Redirect user with active session in internal identity provider to provided return url.
     * @throws ApiError
     */
    login({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/sso/self/callback', body: requestBody, mediaType: 'application/json', errors: {
                400: `Provided credentials for login are invalid.`,
            } }, options)), options);
    }
    /**
     * Callback endpoint for federated SAML identity provider
     * @returns void
     * @throws ApiError
     */
    loginWithSaml(options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/sso/saml/callback', errors: {
                302: `Redirect user from federated SAML identity provider to provided return url.`,
            } }, options)), options);
    }
    /**
     * Get redirect to specific OIDC provider authorization endpoint
     * @returns void
     * @throws ApiError
     */
    oidc({ providerId, state, loginHint, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/sso/oidc', query: {
                'providerId': providerId,
                'loginHint': loginHint,
                'state': state,
            }, errors: {
                302: `Target OIDC provider.`,
                400: `Invalid providerId.`,
                404: `ProviderId doesn't exist.`,
            } }, options)), options);
    }
    /**
     * Get login initialization config
     * @returns any Identity registered.
     * @throws ApiError
     */
    registerIdentity({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities', body: requestBody, mediaType: 'application/json', errors: {
                400: `Provided credentials for registry are invalid.`,
            } }, options)), options);
    }
    /**
     * Send email with recovery code
     * @returns RecoveryEmailResult Email has been successfully sent.
     * @throws ApiError
     */
    postRecoveryEmail({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/recovery', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid email or flow ID provided.`,
            } }, options)), options);
    }
    /**
     * Get password recovery code validation result
     * @returns RecoveryCodeResult Valid recovery code provided.
     * @throws ApiError
     */
    postRecoveryCode({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/recovery/code', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid recovery code provided.`,
            } }, options)), options);
    }
    /**
     * Update the password based on identity token obtained from recovery code
     * @returns void
     * @throws ApiError
     */
    postNewPassword({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/recovery/password', body: requestBody, mediaType: 'application/json', errors: {
                403: `Invalid identity token provided.`,
            } }, options)), options);
    }
    /**
     * Send email verification again
     * @returns void
     * @throws ApiError
     */
    resendEmailVerification({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/verify', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid email verification code provided.`,
            } }, options)), options);
    }
    /**
     * Callback email verification
     * @returns VerificationResult Identity email address verified.
     * @throws ApiError
     */
    completeEmailVerification({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/verify/code', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid email verification code provided.`,
            } }, options)), options);
    }
    /**
     * Send identities link code to confirm linking flow
     * @returns LinkIdentityResult Valid identity link code provided. Identities linked successfully.
     * @throws ApiError
     */
    linkIdentities({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/link', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid identity link code provided.`,
            } }, options)), options);
    }
    /**
     * Send (Resend) identities link code
     * @returns void
     * @throws ApiError
     */
    resendLinkIdentities({ requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/api/identities/resend-link', body: requestBody, mediaType: 'application/json', errors: {
                400: `Invalid identity link code provided.`,
            } }, options)), options);
    }
}
//# sourceMappingURL=AuthService.js.map